import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Self-contained exam countdown.
 *
 * The per-second tick lives in THIS component's state, so it never re-renders
 * the parent — and therefore never re-renders the pdf.js canvas. Previously the
 * countdown was parent state, so every tick re-rendered ExamPortal, which
 * handed PdfExamViewer a fresh watermark object and forced a full canvas
 * re-render once a second: the flicker.
 *
 * `seedSeconds` is the server's authoritative remaining time (from start_exam
 * and the 60s resync poll). Whenever it changes, the countdown re-seeds — so a
 * faculty-granted extension pulls the timer back up without a page reload.
 * The client tick between seeds is display-only; the server enforces the real
 * deadline on the question-PDF route.
 *
 * `onExpire` fires once when the countdown reaches zero, which is what lets the
 * parent hide the question paper immediately instead of waiting for a refresh.
 */
export default function ExamCountdown({ seedSeconds, onExpire }) {
  const [seconds, setSeconds] = useState(seedSeconds ?? null);

  // Keep the latest callback without making it an effect dependency — otherwise
  // a new inline handler from the parent would restart the interval.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Re-seed from the server whenever it reports a fresh value.
  useEffect(() => {
    if (seedSeconds !== null && seedSeconds !== undefined) {
      setSeconds(seedSeconds);
    }
  }, [seedSeconds]);

  // One interval for the whole run: the updater is functional, so this effect
  // does not need `seconds` as a dependency and is not rebuilt on every tick.
  const running = seconds !== null;
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      setSeconds((prev) => {
        if (prev === null) return prev;
        return prev <= 1 ? 0 : prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  // Fires once per transition to zero (re-seeding to 0 leaves it unchanged).
  useEffect(() => {
    if (seconds === 0) {
      onExpireRef.current?.();
    }
  }, [seconds]);

  const low = (seconds ?? 0) < 300;

  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
        low
          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
      }`}
    >
      <Clock size={18} />
      <span className="font-mono font-semibold" data-testid="exam-timer">
        {seconds !== null ? formatTime(seconds) : '--:--'}
      </span>
    </div>
  );
}
